import React, { useState } from "react";
import { Popover, Tabs } from "antd";
import { LogoutOutlined, SettingFilled } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLoginContext } from "src/contextApi/LoginContext";
import { getUserShortName, getUserDisplayName } from "src/util/util.service";
import { CustomTooltip } from "src/components/ui";
import { ROUTE_CONSTANTS } from "src/routes/routeConstant";
import { cn } from "src/util/cn";
import { requestGuardedNavigation } from "../util/navigationGuard";

interface CustomProfileIconProps {
  children: string;
}

const CustomProfileIcon: React.FC<CustomProfileIconProps> = ({ children }) => {
  return (
    <div
      className={cn(
        "flex items-center justify-center",
        "w-10 h-10 rounded-full",
        "bg-[#3867d6] text-white",
        "font-semibold cursor-pointer",
        "select-none"
      )}
    >
      {children}
    </div>
  );
};

export const ProfileMenu: React.FC = () => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { doLogout } = useLoginContext();

  const shortName = getUserShortName();
  const displayName = getUserDisplayName();

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await doLogout();
      navigate(ROUTE_CONSTANTS.LOGIN);
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const onSettingsBtnClick = () => {
    requestGuardedNavigation(() => {
      navigate(ROUTE_CONSTANTS.SETTINGS);
    });
  };

  const popoverContent = (
    <div className="w-[280px] py-4 ">
      <div className="flex items-center justify-between px-3">
        <div className="flex items-center">
          <CustomProfileIcon>{shortName}</CustomProfileIcon>
          <div className="pl-3">
            <p className="font-medium m-0">{displayName}</p>
          </div>
        </div>
        <CustomTooltip title={t("auth.logout")} placement="bottom" className="text-dark">
          <LogoutOutlined
            className={cn("mr-3 text-lg text-red-400 cursor-pointer", {
              "opacity-50 cursor-not-allowed": isLoggingOut,
            })}
            onClick={() => {
              if (isLoggingOut) return;
              requestGuardedNavigation(handleLogout);
            }}
          />
        </CustomTooltip>
      </div>
      <div className="px-2 mt-4 admin-tabs -mb-3">
        <Tabs
          items={[
            {
              key: "settings",
              label: (
                <p className="w-full rounded-sm m-0">
                  <SettingFilled className="!mr-[5px] rounded-sm text-[14px]" />
                  <span>{t("settings")}</span>
                </p>
              ),
              children: <div />,
            },
          ]}
          onTabClick={onSettingsBtnClick}
        />
      </div>
    </div>
  );

  return (
    <Popover content={popoverContent} trigger="click" placement="bottomRight">
      <div className="pr-3">
        <CustomProfileIcon>{shortName}</CustomProfileIcon>
      </div>
    </Popover>
  );
};

export default ProfileMenu;
